import { z } from 'zod';
import { IWorkerMergedTag } from './IWorkerMergedTag';
import { IManagerMergedTag } from './IManagerMergedTag';
import { IUtilityMergedTag } from './IUtilityMergedTag';
import { IMinimizedProfession } from './IMinimizedProfession';
import { minimizedProfessionSchema } from './IMinimizedProfession';
import { tagCertificateSchema } from './ITagCertificate';
import { TagStatuses } from '../constants/statuses.const';
import { AnchorTypes } from '../constants/anchors.const';
import { SafetyViolationTypes } from '../constants/safety.const';
import { UtilityTypes } from '../constants/utilityTypes.enum';
import { EquipmentTypes } from './Equipment/EquipmentTypes';
import { AreaTypeEnum } from './AreaType.enum';
import { dateField, phoneNumberField } from '../schemas/common.schemas';

// Simplified schema — used by contracts/repositories that only need common display fields
export const mergedTagSchema = z.object({
	_id: z.string().optional(),
	tagId: z.string(),
	projectId: z.string(),
	type: z.string().optional(),
	floorId: z.string().optional(),
	floorNick: z.string().optional(),
	areaId: z.string().optional(),
	areaNick: z.string().optional(),
	profession: z.any().optional(),
	name: z.string().optional(),
});

// ============================================================
// Full merged tag schema — covers the complete IMergedTag union
// Uses dateField (accepts both Date and ISO string) for HTTP compatibility.
// Sub-schemas are defined inline to replace z.date() with dateField.
// ============================================================

const translationSchema = z.record(z.string(), z.string());

// IConfigFloor (inline — original uses z.date() for matchingFixedFloorChangeTimestamp)
const httpConfigFloorSchema = z.object({
	projectId: z.string(),
	floorId: z.string(),
	floorNick: z.string(),
	shortFloorNick: z.string(),
	isSpecialFloor: z.boolean(),
	isMovingFloor: z.boolean(),
	isHoist: z.boolean(),
	isGroundFloor: z.boolean(),
	buildingId: z.string().optional(),
	matchingFixedFloorId: z.string().optional(),
	matchingFixedFloorNick: z.string().optional(),
	matchingFixedShortFloorNick: z.string().optional(),
	isMatchingFloorFixed: z.boolean().optional(),
	matchingFixedFloorChangeTimestamp: dateField.optional(),
});

// IStaticAnchor (extends IConfigArea = IConfigFloor + IBaseArea + anchor fields)
const httpStaticAnchorSchema = httpConfigFloorSchema.extend({
	areaId: z.string(),
	areaNick: z.string(),
	areaType: z.enum(AreaTypeEnum).optional(),
	areaSortIndex: z.number().optional(),
	anchorId: z.string(),
	anchorNick: z.string(),
	anchorType: z.enum(AnchorTypes),
	safetyViolations: z.array(z.enum(SafetyViolationTypes)),
});

// IMergedEquipment reporter
const httpEquipmentReporterSchema = z.object({
	areaId: z.string(),
	areaNick: z.string(),
	tagId: z.string(),
	profession: z.string().nullable(),
	rssi: z.string(),
	timestamp: dateField,
	floorId: z.string(),
	floorNick: z.string(),
});

// IMergedEquipment
const httpMergedEquipmentSchema = z.object({
	areaId: z.string(),
	areaNick: z.string(),
	floorId: z.string(),
	floorNick: z.string(),
	_id: z.string().optional(),
	company: z.string(),
	serialNumber: z.string(),
	name: z.string(),
	type: z.enum(EquipmentTypes),
	typeNick: z.string(),
	equipmentId: z.string(),
	projectId: z.string(),
	lastSeenDate: dateField,
	uuid: z.string(),
	batteryLevel: z.string(),
	RSSI: z.string(),
	reporters: z.array(httpEquipmentReporterSchema),
	unSureLocationCounter: z.number().optional(),
	reporterTagIds: z.array(z.string()),
});

// IManager (inline — original uses z.date() for dateCreated/dateDeleted)
const httpManagerSchema = z.object({
	_id: z.string().optional(),
	tradeGroup: z.string(),
	tradeId: z.string(),
	backgroundColor: z.string(),
	sortIndex: z.number(),
	tradeTranslations: translationSchema.optional(),
	tradeGroupTranslations: translationSchema.optional(),
	projectId: z.string(),
	organizationId: z.string(),
	name: z.string(),
	phoneNumber: phoneNumberField,
	receiveNotifications: z.boolean().optional(),
	dateCreated: dateField,
	dateDeleted: dateField.optional(),
});

// IUtility
const httpUtilitySchema = z.object({
	_id: z.string(),
	tradeGroup: z.string(),
	tradeId: z.string(),
	backgroundColor: z.string(),
	sortIndex: z.number(),
	tradeTranslations: translationSchema.optional(),
	tradeGroupTranslations: translationSchema.optional(),
	type: z.enum(UtilityTypes),
	projectId: z.string(),
	profession: minimizedProfessionSchema.optional(),
	name: z.string().optional(),
});

// Shared base: IBaseTag + IBaseStaticTag + IDislocatedTag + IBaseMergeTag
const mergedTagBaseSchema = z.object({
	// IBaseTag
	tagId: z.string(),
	tagNick: z.string(),
	// IBaseStaticTag
	_id: z.string().optional(),
	lastUpdateDate: dateField,
	organizationId: z.string(),
	originProjectId: z.string(),
	certificates: z.array(tagCertificateSchema).optional(),
	tagStatus: z.nativeEnum(TagStatuses),
	// IDislocatedTag
	uuid: z.string(),
	batteryLevel: z.string().optional(),
	isSleeping: z.boolean().optional(),
	// IBaseMergeTag
	projectId: z.string(),
	tagStatusChangedTimestamp: z.number(),
	shouldBeIncluded: z.boolean(),
	shouldBeVisible: z.boolean(),
	closestAnchor: httpStaticAnchorSchema,
	closestEquipment: z.array(httpMergedEquipmentSchema),
	timestamp: dateField,
	wakeUpTimestamp: dateField,
	runnerUpAnchors: z.array(httpStaticAnchorSchema),
	shouldBeIncludedChangeTimestamp: z.number(),
	shouldBeVisibleChangeTimestamp: z.number(),
});

// IWorkerMergedTag: IWorkerStaticTag + IBaseMergeTag
export const workerMergedTagSchema = mergedTagBaseSchema.extend({
	profession: minimizedProfessionSchema,
	name: z.string().optional(),
	phoneNumber: phoneNumberField,
	idNumber: z.string().optional(),
});

// IManagerMergedTag: IManagerStaticTag + IBaseMergeTag
export const managerMergedTagSchema = mergedTagBaseSchema.extend({
	manager: httpManagerSchema,
});

// IUtilityMergedTag: IUtilityStaticTag + IBaseMergeTag
const utilityMergedTagSchema = mergedTagBaseSchema.extend({
	linkedUtility: httpUtilitySchema,
});

export const fullMergedTagSchema = z.union([workerMergedTagSchema, managerMergedTagSchema, utilityMergedTagSchema]);

// Types (union type kept for backward compatibility)
export type IMergedTag<T extends IMinimizedProfession> = IWorkerMergedTag<T> | IManagerMergedTag | IUtilityMergedTag<T>;
